import React, { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, BookOpen } from "lucide-react";
import api, { API, apiError } from "@/lib/api";

const GENERIC = [
  "How do I calibrate the scale on a blueprint?",
  "How does progress billing work?",
  "How do I send an Application for Payment to my client?",
  "What's the difference between the plans?",
];

function contextFor(pathname) {
  if (pathname.startsWith("/app/project/")) {
    return {
      label: "a project (takeoff, estimating & billing)",
      suggestions: [
        "How do I calibrate the scale on a blueprint?",
        "How do I use AI to extract quantities?",
        "How do I build a progress billing application?",
        "How do I send an Application for Payment to my client?",
      ],
    };
  }
  if (pathname.startsWith("/app/settings")) {
    return {
      label: "Settings (branding & account)",
      suggestions: [
        "How do I add my company logo to PDFs?",
        "How do I change my password?",
        "What company details appear on my invoices?",
        "How is my AI usage counted?",
      ],
    };
  }
  if (pathname.startsWith("/app/account")) {
    return {
      label: "Billing & Plan",
      suggestions: [
        "What's the difference between the plans?",
        "How do I upgrade my plan?",
        "How do I cancel my subscription?",
        "What happens to my data if I cancel?",
      ],
    };
  }
  if (pathname.startsWith("/app/insights")) {
    return { label: "the admin Insights dashboard", suggestions: GENERIC };
  }
  return {
    label: "the Projects dashboard",
    suggestions: [
      "How do I create a new project?",
      "How do I archive or duplicate a project?",
      "How do I upload a blueprint?",
      "What's the difference between the plans?",
    ],
  };
}

const HelpAssistant = () => {
  const location = useLocation();
  const ctx = contextFor(location.pathname);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [...prev, { id: assistantId, role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("bs_token")}` },
        body: JSON.stringify({ message: msg, session_id: sessionId, history, context: ctx.label }),
      });
      const sid = res.headers.get("X-Session-Id");
      if (sid) setSessionId(sid);
      if (!res.ok || !res.body) {
        let detail = "The help assistant is unavailable right now. Please try again.";
        try { detail = apiError((await res.json())?.detail); } catch { /* keep default */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split("\n\n");
        buf = blocks.pop() || "";
        for (const block of blocks) {
          const line = block.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "{}") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.t) {
              acc += obj.t;
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: acc } : m)));
            }
          } catch { /* ignore malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: e.message || "Sorry — I couldn't reach the assistant. Please try again.", error: true } : m)));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="help-assistant-toggle"
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-[9999] flex items-center gap-2 rounded-full bg-[#1E3A5F] text-white pl-4 pr-5 py-3 shadow-lg hover:bg-blue-900 transition-colors"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm font-semibold">Help</span>
        </button>
      )}

      {open && (
        <div
          data-testid="help-assistant-panel"
          className="fixed bottom-20 right-4 left-4 sm:left-auto lg:bottom-6 lg:right-6 z-[9999] flex flex-col w-auto sm:w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-3rem)] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#1E3A5F] text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">Blue — Help Assistant</p>
                <p className="text-[11px] text-blue-200 leading-tight">Ask me anything about TradeOff</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-assistant-close" className="text-blue-100 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="help-assistant-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  👋 Hi, I'm Blue. I can help you use TradeOff. Since you're on <span className="font-semibold text-slate-800">{ctx.label}</span>, you might want to ask:
                </p>
                <div className="space-y-2">
                  {ctx.suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`help-suggestion-${i}`}
                      className="w-full text-left text-sm px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <Link
                  to="/app/help"
                  onClick={() => setOpen(false)}
                  data-testid="help-open-centre"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 mt-1"
                >
                  <BookOpen className="h-3.5 w-3.5" /> Browse the full Help Centre
                </Link>
              </div>
            )}
            {messages.map((m) => (
              (m.role === "assistant" && !m.content && !m.error) ? null : (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`help-message-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : m.error
                      ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
              )
            ))}
            {loading && !messages[messages.length - 1]?.content && (
              <div className="flex justify-start" data-testid="help-assistant-typing">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Type your question…"
                data-testid="help-assistant-input"
                className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-28"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="help-assistant-send"
                className="shrink-0 h-10 w-10 rounded-xl bg-[#1E3A5F] text-white flex items-center justify-center disabled:opacity-40 hover:bg-blue-900 transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpAssistant;
